// Política de contraseñas del cliente, espejo de auth.ValidatePassword del backend
// (backend/internal/auth/password.go). El servidor es el guardián real; aquí solo
// guiamos al usuario en vivo (checklist) y anticipamos el rechazo (zod). El texto
// canónico y las reglas DEBEN mantenerse alineados con el backend (SPEC #198).

export const MIN_PASSWORD_LEN = 8;
export const MAX_PASSWORD_LEN = 128;

// REQUISITOS_PASSWORD es el texto canónico de la política mostrado al usuario. Espeja
// auth.RequisitosPassword del backend (que va en minúscula inicial por ser cadena de
// error de Go); aquí arranca en mayúscula por presentarse como frase en la UI.
export const REQUISITOS_PASSWORD =
  "La contraseña debe tener entre 8 y 128 caracteres e incluir al menos una letra y un número (se permiten símbolos, pero no caracteres de control)";

// esControl detecta un carácter de control (C0 0x00–0x1F y C1 0x7F–0x9F). Se comprueba
// por punto de código en lugar de con una regex de rango de control (que biome rechaza).
function esControl(codePoint: number): boolean {
  return codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
}

export type PasswordChecks = {
  longitud: boolean;
  letra: boolean;
  numero: boolean;
  sinControl: boolean;
};

// evaluarPassword calcula, campo a campo, qué requisitos cumple la contraseña. Lo
// consume la checklist en vivo. Cuenta puntos de código (spread) para tratar los
// caracteres fuera del BMP como uno solo, igual que el recuento por runas del backend.
export function evaluarPassword(pw: string): PasswordChecks {
  const longitud = [...pw].length;
  return {
    longitud: longitud >= MIN_PASSWORD_LEN && longitud <= MAX_PASSWORD_LEN,
    letra: /\p{L}/u.test(pw),
    numero: /\p{Nd}/u.test(pw),
    sinControl: pw.length > 0 && [...pw].every((c) => !esControl(c.codePointAt(0) ?? 0)),
  };
}

// passwordValida resume evaluarPassword en un booleano (lo usa el zod de los formularios).
export function passwordValida(pw: string): boolean {
  const c = evaluarPassword(pw);
  return c.longitud && c.letra && c.numero && c.sinControl;
}
